import {
  Image,
  Modal,
  Text,
  TouchableOpacity,
  View,
  ScrollView,
  TextInput,
} from "react-native";
import React, { useState, useRef } from "react";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppImages } from "../../utility/AppImages";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { LinearGradient } from "expo-linear-gradient";
import { ShowLoader } from "../../common/ShowLoader";
import CashOutDetailsModal from "./CashOutDetailsPopup";
import { useAuthProvider } from "../../context/AuthContext";

interface RedeemCreditsModalProps {
  showModal: boolean;
  setShowModal: (value: boolean) => void;
  userProfileData?: {
    credits?: number;
    balance?: number;
    dailyCashoutAmount?: number;
  };
  accessToken?: string;
  setRefreshAPICall?: (value: boolean) => void;
}

const RedeemCreditsModal: React.FC<RedeemCreditsModalProps> = ({
  showModal,
  setShowModal,
  userProfileData,
  accessToken,
  setRefreshAPICall,
}) => {
  const deviceSize = useDeviceSize();
  const styles = useStyles();
  const [count, setCount] = useState(0);
  const [inputValue, setInputValue] = useState("£ 0.00");
  const [showLoader, setShowLoader] = useState(false);
  const [isCashOutModalVisible, setIsCashOutModalVisible] = useState(false);
  const { isActiveSubscriptionFound } = useAuthProvider();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const availableCredits = userProfileData?.credits || 0;
  const availableBalance = userProfileData?.balance || 0;
  const creditToPoundRate = 0.01; // 1 credit = £0.01
  const maxRedeemable = availableCredits * creditToPoundRate;
  const maxAmount = maxRedeemable; // No cashout limit, max is based on available credits
  const platformFee = isActiveSubscriptionFound ? 0.05 : 0.2; // 5% or 20%
  const payoutTime = isActiveSubscriptionFound ? "6 hrs" : "48 hrs";

  const attemptedAmount = parseFloat(inputValue.replace(/£/g, "").trim()) || 0;
  const isButtonDisabled = attemptedAmount <= 0 || attemptedAmount > maxAmount;

  const onCloseClick = () => {
    setShowModal(false);
    setCount(0);
    setInputValue("£ 0.00");
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const updateCount = (newCount: number) => {
    const validCount = parseFloat(newCount.toFixed(2));
    setCount(validCount);
    setInputValue(`£ ${validCount.toFixed(2)}`);
  };

  const handleInputChange = (text: string) => {
    // Remove £ symbol and spaces for processing
    const cleanedText = text.replace(/£/g, "").trim().replace(/[^0-9.]/g, "");

    // Prevent multiple decimal points
    const decimalCount = (cleanedText.match(/\./g) || []).length;
    if (decimalCount > 1) return;

    // Update display with £ symbol
    setInputValue(`£ ${cleanedText}`);

    // Validate and update count temporarily (allow exceeding)
    const numValue = parseFloat(cleanedText) || 0;
    setCount(numValue);

    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout to snap to max if exceeded after 2 seconds
    timeoutRef.current = setTimeout(() => {
      // Parse the current input value (remove £)
      const currentNum = parseFloat(inputValue.replace(/£/g, "").trim()) || 0;
      if (currentNum > maxAmount) {
        updateCount(maxAmount);
      }
    }, 2000);
  };

  const handleInputBlur = () => {
    // Clear timeout on blur
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Snap to max immediately on blur
    const numValue = parseFloat(inputValue.replace(/£/g, "").trim()) || 0;
    const finalValue = Math.min(Math.max(numValue, 0), maxAmount);
    updateCount(finalValue);
  };

  const increment = () => {
    if (count < maxAmount) {
      const newCount = Math.min(count + 1, maxAmount);
      updateCount(newCount);
    }
  };

  const decrement = () => {
    if (count > 0) {
      const newCount = Math.max(count - 1, 0);
      updateCount(newCount);
    }
  };

  const calculateAfterFees = () => {
    const afterFee = count * (1 - platformFee);
    return afterFee.toFixed(2);
  };

  const calculateCredits = () => {
    return Math.round(count / creditToPoundRate);
  };

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={showModal}
      onRequestClose={onCloseClick}
    >
      <View style={styles.container}>
        <ScrollView
          contentContainerStyle={{
            flexGrow: 1,
            justifyContent: "center",
          }}
          style={styles.scrollView}
        >
          <LinearGradient
            colors={["#111827", "#000"]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.mainView}
          >
            {/* Header */}
            <View style={styles.headerView}>
              <Text style={[AppFontStyle.BOLD_ANYBODY_24, styles.headerText]}>
                Redeem Credits
              </Text>
              <TouchableOpacity onPress={onCloseClick} activeOpacity={0.5}>
                <Image
                  source={AppImages.icCloseWhite}
                  style={{
                    height: 28,
                    width: 28,
                    resizeMode: "contain",
                  }}
                />
              </TouchableOpacity>
            </View>

            {/* Separator */}
            <View style={styles.separator} />

            <Text
              style={[
                AppFontStyle.REGULAR_ANYBODY_14,
                {
                  color: AppColors.gray_9CA3AF,
                  marginBottom:
                    deviceSize === DEVICE_SIZES.XS ||
                    deviceSize === DEVICE_SIZES.SM
                      ? 12
                      : 22,
                },
              ]}
            >
              Withdraw your KO credits directly to your wallet.
            </Text>

            {/* Available Credits and Conversion Rate */}
            <View
              style={{
                flexDirection:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? "column"
                    : "row",
                gap:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? 8
                    : 12,
                marginBottom:
                  deviceSize === DEVICE_SIZES.XS ||
                  deviceSize === DEVICE_SIZES.SM
                    ? 12
                    : 22,
                alignSelf: "stretch",
              }}
            >
              {/* Available Credits */}
              <LinearGradient
                colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.infoBox, { flex: 1 }]}
              >
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { color: AppColors.gray_9CA3AF },
                  ]}
                >
                  Available Credits
                </Text>
                <Text style={[AppFontStyle.BOLD_ANYBODY_32, {}]}>
                  {availableCredits}
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_12,
                    { color: AppColors.gray_9CA3AF },
                  ]}
                >
                  ≈ £{(availableCredits * creditToPoundRate).toFixed(2)}
                </Text>
              </LinearGradient>

              {/* Conversion Rate */}
              <LinearGradient
                colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={[styles.infoBox, { flex: 1 }]}
              >
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { color: AppColors.gray_9CA3AF },
                  ]}
                >
                  Conversion Rate
                </Text>
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 11,
                  }}
                >
                  <Text style={[AppFontStyle.BOLD_ANYBODY_32, {}]}>1</Text>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: AppColors.gray_9CA3AF },
                    ]}
                  >
                    Credit
                  </Text>
                  <Text style={[AppFontStyle.BOLD_ANYBODY_24, {}]}>=</Text>
                  <Text style={[AppFontStyle.BOLD_ANYBODY_32, {}]}>
                    £{creditToPoundRate.toFixed(2)}
                  </Text>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { color: AppColors.gray_9CA3AF },
                    ]}
                  >
                    Pound
                  </Text>
                </View>
              </LinearGradient>
            </View>

            {/* Amount to Cash Out */}
            <LinearGradient
              colors={["rgba(220, 38, 38, 0.01)", "rgba(17, 24, 39, 0.50)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.cashOutBox}
            >
              <Text
                style={[
                  AppFontStyle.SEMIBOLD_ANYBODY_16,
                  {
                    textAlign: "center",
                    marginBottom:
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? 12
                        : 18,
                  },
                ]}
              >
                Amount to Cash Out
              </Text>

              <View style={styles.counterWrapper}>
                <TouchableOpacity
                  style={[styles.button, count === 0 && styles.disabledButton]}
                  onPress={decrement}
                  disabled={count === 0}
                  activeOpacity={0.7}
                >
                  <Image
                    source={AppImages.icMinusIcon}
                    style={{
                      width: 18,
                      height: 3,
                      tintColor: AppColors.purple_453B50,
                    }}
                  />
                </TouchableOpacity>

                <View style={styles.inputWrapper} pointerEvents="box-none">
                  <TextInput
                    style={[
                      deviceSize === DEVICE_SIZES.XS ||
                      deviceSize === DEVICE_SIZES.SM
                        ? AppFontStyle.BOLD_ANYBODY_24
                        : AppFontStyle.BOLD_ANYBODY_32,
                      styles.amountInput,
                    ]}
                    value={inputValue}
                    onChangeText={handleInputChange}
                    onBlur={handleInputBlur}
                    keyboardType="decimal-pad"
                    placeholder="£ 0.00"
                    placeholderTextColor={AppColors.gray_9CA3AF}
                    maxLength={15}
                  />
                </View>

                <TouchableOpacity
                  style={[
                    styles.button,
                    count >= maxAmount && styles.disabledButton,
                  ]}
                  onPress={increment}
                  disabled={count >= maxAmount}
                  activeOpacity={0.7}
                >
                  <Image
                    source={AppImages.icPlusIcon}
                    style={{
                      width: 18,
                      height: 18,
                      tintColor: AppColors.purple_453B50,
                    }}
                  />
                </TouchableOpacity>
              </View>

              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  { color: AppColors.gray_9CA3AF, textAlign: "center" },
                ]}
              >
                ≈ {calculateCredits()} KO Credits
              </Text>

              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_14,
                  {
                    color: AppColors.green_00B536,
                    textAlign: "center",
                  },
                ]}
              >
                After fees: £{calculateAfterFees()} (
                {(platformFee * 100).toFixed(0)}% fee deducted)
              </Text>

              <LinearGradient
                colors={["#DC2626", "#991B1B"]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 0 }}
                style={[
                  styles.getPaidButton,
                  isButtonDisabled && styles.disabledButton,
                ]}
              >
                <TouchableOpacity
                  onPress={() => {
                    if (!isButtonDisabled) {
                      setIsCashOutModalVisible(true);
                    }
                  }}
                  disabled={isButtonDisabled}
                  style={{
                    width: "100%",
                    height: "100%",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Text
                    style={[
                      AppFontStyle.BOLD_ANYBODY_16,
                      {
                        color: isButtonDisabled
                          ? AppColors.gray_9CA3AF
                          : AppColors.white,
                      },
                    ]}
                  >
                    Get Paid
                  </Text>
                </TouchableOpacity>
              </LinearGradient>
            </LinearGradient>

            {/* Subscription Plans - Hidden for now */}
            {/* <LinearGradient
              colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.subscriptionBox}
            >
              <View style={styles.tableHeader}>
                <Text style={[AppFontStyle.MEDIUM_ANYBODY_14, { flex: 1 }]}>
                  Plan
                </Text>
                <Text
                  style={[
                    AppFontStyle.MEDIUM_ANYBODY_14,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  Platform Fee
                </Text>
                <Text
                  style={[
                    AppFontStyle.MEDIUM_ANYBODY_14,
                    { flex: 1, textAlign: "right" },
                  ]}
                >
                  Payout Time
                </Text>
              </View>

              <View style={styles.tableRow}>
                <View style={{ flexDirection: "row", alignItems: "center", flex: 1, gap: 8 }}>
                  <Text style={[AppFontStyle.REGULAR_ANYBODY_14, {}]}>
                    🪙 Non-Subscriber
                  </Text>
                  {!isActiveSubscriptionFound && (
                    <View style={styles.currentBadge}>
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_10,
                          { color: AppColors.red_DC2626 },
                        ]}
                      >
                        Current
                      </Text>
                    </View>
                  )}
                </View>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  20%
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "right" },
                  ]}
                >
                  48 hrs
                </Text>
              </View>

              <View style={styles.tableRow}>
                <View style={{ flexDirection: "row", alignItems: "center", flex: 1, gap: 8 }}>
                  <Text style={[AppFontStyle.REGULAR_ANYBODY_14, {}]}>
                    🎟️ Monthly
                  </Text>
                  {isActiveSubscriptionFound && (
                    <View style={styles.currentBadge}>
                      <Text
                        style={[
                          AppFontStyle.BOLD_ANYBODY_10,
                          { color: AppColors.red_DC2626 },
                        ]}
                      >
                        Current
                      </Text>
                    </View>
                  )}
                </View>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  12%
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "right" },
                  ]}
                >
                  24 hrs
                </Text>
              </View>

              <View style={styles.tableRow}>
                <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { flex: 1 }]}>
                  💎 Quarterly
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  8%
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "right" },
                  ]}
                >
                  12 hrs
                </Text>
              </View>

              <View style={styles.tableRow}>
                <Text style={[AppFontStyle.REGULAR_ANYBODY_14, { flex: 1 }]}>
                  🚀 Annual
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "center" },
                  ]}
                >
                  5%
                </Text>
                <Text
                  style={[
                    AppFontStyle.REGULAR_ANYBODY_14,
                    { flex: 1, textAlign: "right" },
                  ]}
                >
                  6 hrs
                </Text>
              </View>

              {!isActiveSubscriptionFound && (
                <TouchableOpacity style={styles.upgradeButton}>
                  <Text
                    style={[
                      AppFontStyle.MEDIUM_ANYBODY_14,
                      { color: AppColors.white },
                    ]}
                  >
                    ⚡ Upgrade
                  </Text>
                </TouchableOpacity>
              )}

              <Text
                style={[
                  AppFontStyle.REGULAR_ANYBODY_12,
                  { color: AppColors.gray_9CA3AF, textAlign: "center", marginTop: 12 },
                ]}
              >
                Upgrade your plan to reduce fees and get faster payouts.
              </Text>
            </LinearGradient> */}
          </LinearGradient>
        </ScrollView>
      </View>

      <CashOutDetailsModal
        visible={isCashOutModalVisible}
        onClose={() => {
          if (setRefreshAPICall) {
            setRefreshAPICall(true);
          }
          setIsCashOutModalVisible(false);
          setShowModal(false);
          setCount(0);
          setInputValue("£ 0.00");
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
        }}
        accessToken={accessToken}
        totalAmount={count}
        setIsCashOutModalVisible={setIsCashOutModalVisible}
        userProfileData={userProfileData}
      />

      <ShowLoader showIndicater={showLoader} />
    </Modal>
  );
};

export default RedeemCreditsModal;

const useStyles = CreateResponsiveStyle(
  {
    container: {
      flex: 1,
      backgroundColor: AppColors.blackTrans_000000a8,
      justifyContent: "center",
      alignItems: "center",
    },
    scrollView: {
      width: "100%",
    },
    mainView: {
      width: "90%",
      maxWidth: 680,
      padding: 24,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 22,
      alignSelf: "center",
      borderRadius: 8,
      borderWidth: 1.6,
      borderColor: "rgba(239, 68, 68, 0.50)",
      marginVertical: 30,
    },
    headerView: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      alignSelf: "stretch",
    },
    headerText: {
      color: AppColors.white,
    },
    separator: {
      height: 1,
      backgroundColor: "#838383",
      alignSelf: "stretch",
      marginBottom: 12,
    },
    infoBox: {
      display: "flex",
      height: 127,
      padding: 16,
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 11,
      borderRadius: 8,
      borderWidth: 0.8,
      borderColor: "#374151",
    },
    cashOutBox: {
      display: "flex",
      padding: 16,
      flexDirection: "column",
      alignItems: "center",
      gap: 18,
      alignSelf: "stretch",
      borderRadius: 8,
      borderWidth: 0.5,
      borderColor: "#DC2626",
    },
    counterWrapper: {
      flexDirection: "row",
      alignItems: "center",
      backgroundColor: AppColors.black_040404,
      borderRadius: 40,
      height: 80,
      justifyContent: "space-between",
      paddingHorizontal: 8,
      alignSelf: "center",
      maxWidth: 300,
      width: "100%",
    },
    inputWrapper: {
      flex: 1,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: 16,
      zIndex: 1,
    },
    amountInput: {
      color: AppColors.white,
      textAlign: "center",
      padding: 0,
      flex: 1,
      zIndex: 1,
    },
    button: {
      width: 36.2,
      height: 36.2,
      borderRadius: 18.1,
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "white",
      flexShrink: 0,
      zIndex: 10,
    },
    disabledButton: {
      backgroundColor: "gray",
    },
    getPaidButton: {
      display: "flex",
      width: 158,
      height: 42,
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 6,
    },
    subscriptionBox: {
      display: "flex",
      padding: 12,
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      gap: 14,
      alignSelf: "stretch",
      borderRadius: 8,
      borderWidth: 1,
      borderColor: "#374151",
    },
    tableHeader: {
      flexDirection: "row",
      alignItems: "center",
      alignSelf: "stretch",
      paddingBottom: 8,
      borderBottomWidth: 1,
      borderBottomColor: AppColors.gray_374151,
    },
    tableRow: {
      flexDirection: "row",
      alignItems: "center",
      alignSelf: "stretch",
      paddingVertical: 8,
    },
    currentBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      backgroundColor: "rgba(220, 38, 38, 0.2)",
      borderRadius: 4,
      borderWidth: 1,
      borderColor: AppColors.buttonRed_DC2626,
    },
    upgradeButton: {
      marginTop: 8,
      paddingVertical: 8,
      paddingHorizontal: 24,
      backgroundColor: AppColors.appViolentColor_6D08C3,
      borderRadius: 6,
    },
  },
  {
    [DEVICE_SIZES.SM]: {
      scrollView: {
        maxHeight: "95%",
      },
      mainView: {
        width: "90%",
        padding: 12,
        gap: 12,
        marginHorizontal: 10,
        marginVertical: 10,
      },
      headerView: {
        marginBottom: 8,
      },
      headerText: {
        fontSize: 20,
      },
      separator: {
        marginBottom: 8,
      },
      infoBox: {
        height: 90,
        padding: 10,
        gap: 6,
      },
      cashOutBox: {
        padding: 12,
        gap: 10,
      },
      counterWrapper: {
        height: 60,
        paddingHorizontal: 6,
      },
      button: {
        width: 36.2,
        height: 36.2,
        borderRadius: 18.1,
      },
      inputWrapper: {
        paddingHorizontal: 8,
      },
      getPaidButton: {
        width: 140,
        height: 38,
      },
    },
    [DEVICE_SIZES.XS]: {
      scrollView: {
        maxHeight: "95%",
      },
      mainView: {
        width: "90%",
        padding: 12,
        gap: 12,
        marginHorizontal: 10,
        marginVertical: 10,
      },
      headerView: {
        marginBottom: 8,
      },
      headerText: {
        fontSize: 20,
      },
      separator: {
        marginBottom: 8,
      },
      infoBox: {
        height: 90,
        padding: 10,
        gap: 6,
      },
      cashOutBox: {
        padding: 12,
        gap: 10,
      },
      counterWrapper: {
        height: 60,
        paddingHorizontal: 6,
      },
      button: {
        width: 36.2,
        height: 36.2,
        borderRadius: 18.1,
      },
      inputWrapper: {
        paddingHorizontal: 8,
      },
      getPaidButton: {
        width: 140,
        height: 38,
      },
    },
  }
);
